import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { money } from "./roasUtil";

const OWNER_LABEL = { ours: "Ours", client: "Client", test: "Test", "": "Unclassified" };
const OWNER_CLS = { ours: "text-[var(--ok)]", client: "text-[var(--ai)]", test: "text-[var(--text-dim)]", "": "text-[var(--warn)]" };

function Row({ c, checked, onToggle, onChanged }) {
  const setOwner = async (owner) => {
    try { await api.put(`/roas/campaigns/${c.campaign_id}`, { owner }); toast.success(`Marked ${OWNER_LABEL[owner].toLowerCase()}`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${c.name}" and all its spend from the ROAS report? It won't come back on sync unless restored.`)) return;
    try { await api.delete(`/roas/campaigns/${c.campaign_id}`); toast.success("Campaign removed"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete"); }
  };
  return (
    <div className="flex items-center gap-2 text-xs py-1.5 px-1 border-b border-[var(--border)] last:border-0 hover:bg-[var(--panel-2)]" data-testid={`classify-row-${c.campaign_id}`}>
      <input type="checkbox" checked={checked} onChange={onToggle} data-testid={`classify-check-${c.campaign_id}`} />
      <span className="flex-1 min-w-0 cursor-pointer" onClick={onToggle}><span className="block truncate">{c.name}</span>
        <span className="text-[10px] text-[var(--text-faint)]">{c.account_name} · {(c.status || "").toLowerCase()}{c.last_date ? ` · last spend ${c.last_date}` : ""}{c.client_name ? ` · ${c.client_name}` : ""}</span></span>
      <span className="tabular-nums text-[var(--text-dim)] w-24 text-right">{money(c.spend)}</span>
      <select className={`px-1.5 py-0.5 rounded border text-xs bg-transparent w-28 ${OWNER_CLS[c.owner || ""]}`} value={c.owner || ""}
        onChange={(e) => setOwner(e.target.value)} data-testid={`classify-owner-${c.campaign_id}`}>
        {["ours", "client", "test", ""].map((o) => <option key={o || "none"} value={o}>{OWNER_LABEL[o]}</option>)}
      </select>
      <button className="text-[var(--text-faint)] hover:text-[var(--breach)]" onClick={del} title="Delete from report" data-testid={`classify-delete-${c.campaign_id}`}><Trash2 size={13} /></button>
    </div>
  );
}

export function CampaignClassifier({ open, onClose, campaigns, accounts, onChanged }) {
  const [q, setQ] = useState("");
  const [acc, setAcc] = useState("");
  const [show, setShow] = useState("all");
  const [sel, setSel] = useState([]);
  const [client, setClient] = useState("");
  const rows = campaigns.filter((c) => (!acc || c.account_id === acc) && (show === "all" || (c.owner || "") === show)
    && (!q || `${c.name} ${c.client_name || ""}`.toLowerCase().includes(q.toLowerCase())));
  const apply = async (owner, body) => {
    try {
      const r = await api.put("/roas/campaigns/classify", { owner, client_name: owner === "client" ? client : "", ...body });
      toast.success(`${r.data.updated} campaign${r.data.updated === 1 ? "" : "s"} marked ${OWNER_LABEL[owner].toLowerCase()}`);
      setSel([]); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
  };
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] flex flex-col" data-testid="roas-classifier">
        <DialogHeader><DialogTitle>Classify campaigns: ours, a client's or a test</DialogTitle></DialogHeader>
        <div className="text-xs text-[var(--text-dim)] -mt-2">Client campaigns are kept out of your ROAS. Test campaigns are tracked on their own and shown as “ROAS incl. test”. Change any campaign any time with its dropdown.</div>
        <div className="flex flex-wrap gap-2 items-center">
          <input className="px-2.5 py-1.5 rounded border text-sm flex-1 min-w-[160px]" placeholder="Search name" value={q} onChange={(e) => setQ(e.target.value)} data-testid="classify-search" />
          <select className="px-2 py-1.5 rounded border text-xs bg-transparent" value={acc} onChange={(e) => setAcc(e.target.value)} data-testid="classify-account">
            <option value="">All ad accounts</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select className="px-2 py-1.5 rounded border text-xs bg-transparent" value={show} onChange={(e) => setShow(e.target.value)} data-testid="classify-show">
            <option value="all">Any type</option><option value="">Unclassified</option><option value="ours">Ours</option><option value="client">Client</option><option value="test">Test</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2 items-center border-y border-[var(--border)] py-2">
          <button className="text-xs text-[var(--ai)]" onClick={() => setSel(rows.map((c) => c.campaign_id))} data-testid="classify-select-shown">Select {rows.length} shown</button>
          <span className="text-xs text-[var(--text-dim)]">{sel.length} selected</span>
          <input className="px-2 py-1 rounded border text-xs w-36 ml-auto" placeholder="Client name (optional)" value={client} onChange={(e) => setClient(e.target.value)} data-testid="classify-client-name" />
          {["ours", "client", "test", ""].map((o) => (
            <button key={o || "none"} className={`btn text-xs ${o === "ours" ? "btn-primary" : ""}`} disabled={!sel.length} onClick={() => apply(o, { campaign_ids: sel })} data-testid={`classify-set-${o || "none"}`}>{o === "ours" ? "Mark ours" : OWNER_LABEL[o]}</button>
          ))}
          {acc && <button className="btn text-xs" onClick={() => apply("client", { account_id: acc })} data-testid="classify-account-client">Whole account = client</button>}
        </div>
        <div className="overflow-auto flex-1 -mx-1">
          {rows.map((c) => <Row key={c.campaign_id} c={c} checked={sel.includes(c.campaign_id)} onToggle={() => toggle(c.campaign_id)} onChanged={onChanged} />)}
          {!rows.length && <div className="text-sm text-[var(--text-dim)] p-3">No campaigns match. Run “Sync Meta” if this is empty.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
